import * as z from "zod";

import { defaultColorStyles } from "./ConfigContent/FreshpaintConfigContent/sections/FreshpaintThemeConfig/constants";
import { ConsentManagementConfigVersion } from "@/api";
import {
  consentManagementIntegrations,
  ConsentManagementSlug,
  fpConsentManagementCategoryMap,
} from "@/consent-management/consentManagement";
import { DestinationConfigSaasDestinationPair } from "@/hooks/useDestinationConfigSaasDestinationPairs";
import {
  liftDescriptionFromIntroSection,
  type PrefsLikeLanguage,
  sortPreferencesModalSections,
} from "@/consent-management/utils/freshpaint";

const acceptedCssNamedColors = ["transparent"];

export const hexColorRegex = /^#([A-Fa-f0-9]{3,4}){1,2}$/;
const rgbColorRegex = /^rgb\(\s*(\d{1,3}%?\s*,\s*){2}\d{1,3}%?\s*\)$/;
const rgbaColorRegex = /^rgba\(\s*(\d{1,3}%?\s*,\s*){3}(0|1|0?\.\d+)\s*\)$/;
const hslColorRegex = /^hsl\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*\)$/;
const hslaColorRegex =
  /^hsla\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*,\s*(0|1|0?\.\d+)\s*\)$/;

export const validCssColorString = z.string().refine(
  (str) => {
    const s = str.trim().toLowerCase();
    return (
      hexColorRegex.test(s) ||
      rgbColorRegex.test(s) ||
      rgbaColorRegex.test(s) ||
      hslColorRegex.test(s) ||
      hslaColorRegex.test(s) ||
      acceptedCssNamedColors.includes(s)
    );
  },
  { message: "Invalid CSS color value" },
);

export const hexColorString = z
  .string()
  .regex(hexColorRegex, { message: "Must be a valid hex color" });

export const PRIVACY_POLICY_URL_ERROR_MESSAGE =
  "Must be a valid URL (e.g. https://example.com/privacy) or a path (e.g. /privacy)";

/** Accepts empty, absolute, or root-relative URLs such as `/privacy`. */
export function isValidPrivacyPolicyUrl(value: string): boolean {
  if (value === "") return true;
  try {
    new URL(value);
    return true;
  } catch {
    return value.startsWith("/") && !value.startsWith("//");
  }
}

export const privacyPolicyUrlSchema = z
  .string()
  .refine(isValidPrivacyPolicyUrl, {
    message: PRIVACY_POLICY_URL_ERROR_MESSAGE,
  });

const consentModelSchema = z.enum(["opt-in", "opt-out"]);

const translationLanguageKeySchema = z.string();

function createTranslationSchema(requiredCopySchema: z.ZodString) {
  const consentModalCopySchema = z.object({
    title: requiredCopySchema,
    description: z.string(),
    acceptAllBtn: requiredCopySchema,
    acceptNecessaryBtn: requiredCopySchema,
    showPreferencesBtn: requiredCopySchema,
  });

  const preferencesModalCopySchema = z.object({
    title: requiredCopySchema,
    description: z.string(),
    acceptAllBtn: requiredCopySchema,
    acceptNecessaryBtn: requiredCopySchema,
    savePreferencesBtn: requiredCopySchema,
    closeIconLabel: requiredCopySchema,
    sections: z.array(
      z.object({
        title: requiredCopySchema,
        description: requiredCopySchema,
        linkedCategory: requiredCopySchema,
      }),
    ),
  });

  return z.object({
    consentModal: consentModalCopySchema,
    preferencesModal: preferencesModalCopySchema,
  });
}

const translationSchema = createTranslationSchema(z.string().min(1));
const inactiveTranslationSchema = createTranslationSchema(z.string());

const languageSchema = z.object({
  autoDetect: z.string(),
  default: translationLanguageKeySchema,
  translations: z.record(translationLanguageKeySchema, translationSchema),
  privacyPolicyUrl: z.string().optional(),
});

const inactiveLanguageSchema = languageSchema.extend({
  translations: z.record(
    translationLanguageKeySchema,
    inactiveTranslationSchema,
  ),
});

/*
 * Disabled policies hide their copy editor. Keep validating the stored shape,
 * but leave content validation to the active-policy branch so hidden fields
 * cannot block publishing.
 */
const activeConditionalConfigFieldsSchema = z.object({
  language: languageSchema,
  privacyPolicyUrl: privacyPolicyUrlSchema,
});

const configurableCssVariableKeySchema = z.enum([
  "--cc-bg",
  "--cc-primary-color",
  "--cc-secondary-color",
  "--cc-btn-primary-bg",
  "--cc-btn-primary-color",
  "--cc-btn-primary-border-color",
  "--cc-btn-primary-hover-bg",
  "--cc-btn-primary-hover-color",
  "--cc-btn-primary-hover-border-color",
  "--cc-btn-secondary-bg",
  "--cc-btn-secondary-color",
  "--cc-btn-secondary-border-color",
  "--cc-btn-secondary-hover-bg",
  "--cc-btn-secondary-hover-color",
  "--cc-btn-secondary-hover-border-color",
  "--cc-separator-border-color",
  "--cc-toggle-on-bg",
  "--cc-toggle-off-bg",
  "--cc-toggle-on-knob-bg",
  "--cc-toggle-off-knob-bg",
  "--cc-toggle-enabled-icon-color",
  "--cc-toggle-disabled-icon-color",
  "--cc-toggle-readonly-bg",
  "--cc-toggle-readonly-knob-bg",
  "--cc-toggle-readonly-knob-icon-color",
  "--cc-section-category-border",
  "--cc-cookie-category-block-bg",
  "--cc-cookie-category-block-border",
  "--cc-cookie-category-block-hover-bg",
  "--cc-cookie-category-block-hover-border",
  "--cc-cookie-category-expanded-block-bg",
  "--cc-cookie-category-expanded-block-hover-bg",
  "--cc-overlay-bg",
  "--cc-webkit-scrollbar-bg",
  "--cc-webkit-scrollbar-hover-bg",
  "--cc-footer-bg",
  "--cc-footer-color",
  "--cc-footer-border-color",
]);

export type FreshpaintConsentManagerConfigurableCssVariableKey = z.infer<
  typeof configurableCssVariableKeySchema
>;

const consentModalLayoutSchema = z.enum(["box", "bar", "bar inline"]);
export const consentModalPositionSchema = z.enum([
  "top",
  "top left",
  "top center",
  "top right",
  "middle",
  "middle left",
  "middle center",
  "middle right",
  "bottom",
  "bottom left",
  "bottom center",
  "bottom right",
]);
export type ConsentModalPosition = z.infer<typeof consentModalPositionSchema>;

const preferencesModalLayoutSchema = z.enum(["box", "bar"]);
export const preferencesModalPositionSchema = z.enum(["left", "right"]);
export type PreferencesModalPosition = z.infer<
  typeof preferencesModalPositionSchema
>;

const consentModalSchema = z.object({
  layout: consentModalLayoutSchema,
  position: consentModalPositionSchema,
});

const preferencesModalSchema = z.object({
  layout: preferencesModalLayoutSchema,
  position: preferencesModalPositionSchema,
});

const guiOptionsSchema = z.object({
  consentModal: consentModalSchema,
  preferencesModal: preferencesModalSchema,
});

type GuiOptions = z.infer<typeof guiOptionsSchema>;

export const categoryBehaviorSchema = z.enum([
  "optional",
  "alwaysOn",
  "hidden",
]);
export type CategoryBehavior = z.infer<typeof categoryBehaviorSchema>;

// Persisted as the user's intent; runtime booleans (`enabled`, `readOnly`,
// modal visibility) are derived from `behavior + consentModel + essential`
// at the transport boundary in `buildFreshpaintCategories` and
// `filterPreferencesSectionsByDisplay`.
const categorySettingsSchema = z.object({
  behavior: categoryBehaviorSchema,
});

export type CategorySettings = z.infer<typeof categorySettingsSchema>;

const categorySettingsRecordSchema = z.record(
  z.string(),
  categorySettingsSchema,
);

const typeSchema = z.enum(["freshpaint", "onetrust", "osano"]);

const matchConditionSchema = z.object({
  type: z.string(),
  values: z.array(z.string()),
});
const conditionalConfigSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    customCSS: z.string(),
    language: inactiveLanguageSchema,
    guiOptions: guiOptionsSchema,
    consentModel: consentModelSchema,
    matchConditions: z.array(matchConditionSchema),
    autoShow: z.boolean(),
    disablePageInteraction: z.boolean(),
    hideConsentModalRejectAll: z.boolean(),
    enableConsentModalDismiss: z.boolean(),
    editSelections: z.boolean(),
    disabled: z.boolean(),
    categorySettings: categorySettingsRecordSchema,
    privacyPolicyUrl: z.string(),
    privacyPolicyLinkText: z.string().optional(),
  })
  .superRefine((config, ctx) => {
    if (config.disabled) return;

    const result = activeConditionalConfigFieldsSchema.safeParse(config);
    if (result.success) return;

    for (const issue of result.error.issues) {
      ctx.addIssue(issue as z.core.$ZodRawIssue);
    }
  });

export type ConditionalConfig = z.infer<typeof conditionalConfigSchema>;

const geotargetingBehaviorSchema = z.enum(["disabled", "managed", "custom"]);

const freshpaintConfigSchema = z
  .object({
    autoShow: z.boolean(),
    disablePageInteraction: z.boolean(),
    hideConsentModalRejectAll: z.boolean(),
    enableConsentModalDismiss: z.boolean(),
    colorStyles: z.partialRecord(
      configurableCssVariableKeySchema,
      validCssColorString,
    ),
    editSelections: z.boolean(),
    gcmEnabled: z.boolean(),
    guiOptions: guiOptionsSchema,
    iconColor: z.string(),
    iconPosition: z.enum(["left", "right"]).optional(),
    customCSS: z.string(),
    language: languageSchema,
    simpleBackgroundColor: hexColorString,
    simplePrimaryColor: hexColorString,
    simpleSecondaryColor: hexColorString,
    geotargetingBehavior: geotargetingBehaviorSchema,
    conditionalConfigs: z.array(conditionalConfigSchema),
    privacyPolicyUrl: privacyPolicyUrlSchema,
    privacyPolicyLinkText: z.string().optional(),
    categorySettings: categorySettingsRecordSchema,
  })
  .superRefine((config, ctx) => {
    if (!config.editSelections) return;

    const parsed = validCssColorString.safeParse(config.iconColor);
    if (parsed.success) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: parsed.error.issues[0]?.message ?? "Invalid CSS color value",
      path: ["iconColor"],
    });
  });

export const consentManagerConfigSchema = z
  .object({
    type: typeSchema,
    consentModel: consentModelSchema,
    consentMapping: z.record(
      z.string(),
      z.string().min(1, "Must specify a consent category"),
    ),
    categoryMapping: z.record(
      z.string(),
      z.string().min(1, "Must specify a category name"),
    ),
    freshpaintConfig: freshpaintConfigSchema.optional(),
  })
  .refine((data) => {
    if (data.type === "freshpaint") {
      return data.freshpaintConfig !== undefined;
    }
    return true;
  }, "Freshpaint configuration is required for Freshpaint Consent Manager");

export type ConsentManagerConfigSchema = z.infer<
  typeof consentManagerConfigSchema
>;

/**
 * Initial values helper function
 */
export function initialValues(
  consentConfig: ConsentManagementConfigVersion,
  enabledCategorizableDestinations: DestinationConfigSaasDestinationPair[],
  audiencesEnabled: boolean,
  sessionReplayEntitled: boolean,
  webAnalyticsEnabled: boolean,
): ConsentManagerConfigSchema {
  const defaultAnalyticsSlug =
    sessionReplayEntitled || webAnalyticsEnabled
      ? defaultAnalyticsCategory(
          consentConfig.type,
          consentConfig.categoryMapping ?? {},
        )
      : "";

  // Drop empty-string entries from the persisted mapping. Old configs can
  // carry `{ "Some Dest": "" }` when a destination was enabled, autosaved
  // before the user picked a category, then disabled — the entry survives in
  // storage but the destination no longer renders a Controller, so the user
  // has nowhere to fix it. Zod's `min(1)` on the record still rejects the
  // empty value, flipping `formState.isValid` false with no visible error and
  // silently blocking publish. Filtering here doesn't lose data for enabled
  // destinations (the reduce below seeds them with `""` anyway) or for the
  // feature-level keys (their own branches seed the default).
  const persistedConsentMapping = Object.fromEntries(
    Object.entries(consentConfig.consentMapping ?? {}).filter(
      ([, value]) => value !== "",
    ),
  );

  const apiConsentMapping = {
    // Make sure that all enabled destinations are initialized in the form values
    ...enabledCategorizableDestinations.reduce(
      (acc, { destinationConfig }) => {
        acc[destinationConfig.name] = "";
        return acc;
      },
      {} as Record<string, string>,
    ),
    ...(audiencesEnabled ? { Audiences: "" } : {}),
    ...(sessionReplayEntitled
      ? { "Session Replay": defaultAnalyticsSlug }
      : {}),
    ...(webAnalyticsEnabled ? { "Web Analytics": defaultAnalyticsSlug } : {}),
    ...persistedConsentMapping,
  };

  const formConsentMapping = Object.entries(apiConsentMapping).reduce(
    (acc, [key, value]) => {
      acc[formDestinationName(key)] = value;
      return acc;
    },
    {} as Record<string, string>,
  );

  return {
    ...consentConfig,
    consentMapping: formConsentMapping,
    freshpaintConfig: initialFreshpaintConfigValues(consentConfig),
  };
}

// TODO: This input/output conversion would not be necessary if consentMapping used slugs instead of names

export function formDestinationName(destinationName: string): string {
  return destinationName.replace(".", "_dot_");
}

export function apiDestinationName(formDestinationName: string): string {
  return formDestinationName.replace("_dot_", ".");
}

export const ESSENTIAL_CATEGORY_ID = "essential";

/**
 * `essential` always cookies the user as consented and is non-configurable —
 * it cannot be unselected, made editable, or hidden. Exported so consumers
 * (UI and runtime) can rely on the same canonical value.
 */
export const ESSENTIAL_CATEGORY_SETTINGS: CategorySettings = {
  behavior: "alwaysOn",
};

/**
 * Picks the default consent category slug for product controls that should
 * default to the analytics category (Session Replay, Web Analytics + Funnels).
 * Tries the provider-specific analytics slug first (from
 * `fpConsentManagementCategoryMap`), then falls back to the
 * alphabetically-first non-essential category. Returns empty string when
 * the only category is `essential`, signaling the customer must pick
 * something explicitly.
 */
export function defaultAnalyticsCategory(
  type: ConsentManagementSlug,
  categoryMapping: Record<string, string>,
): string {
  const analyticsSlug = fpConsentManagementCategoryMap[type]?.analytics;
  if (analyticsSlug && analyticsSlug in categoryMapping) {
    return analyticsSlug;
  }
  const fallback = Object.keys(categoryMapping)
    .filter((k) => k !== ESSENTIAL_CATEGORY_ID)
    .sort()[0];
  return fallback ?? "";
}

/**
 * Returns the seeded default per-category settings for a config. `essential`
 * is always forced to "alwaysOn" — it is non-configurable. Other categories
 * preserve saved behavior when present (validated through
 * `categorySettingsSchema`) and otherwise default to "optional".
 *
 * Non-essential entries persisted as "alwaysOn" are coerced to "optional".
 * The dropdown does not offer "alwaysOn" for non-essential categories —
 * forcing visitors into a non-essential category is consent theatre — so we
 * coerce on seed to keep form state and persisted payload aligned with the
 * UX without dirtying the form on mount.
 */
export function defaultCategorySettings(
  categoryMapping: Record<string, string> | undefined,
  saved?: Record<string, unknown>,
): Record<string, CategorySettings> {
  const out: Record<string, CategorySettings> = {};

  for (const id of Object.keys(categoryMapping ?? {})) {
    if (id === ESSENTIAL_CATEGORY_ID) {
      out[id] = ESSENTIAL_CATEGORY_SETTINGS;
      continue;
    }

    // Be defensive: `saved` is loaded from JSONB and typed `any` upstream,
    // so a malformed entry shouldn't be silently propagated into form state.
    const parsed = categorySettingsSchema.safeParse(saved?.[id]);
    if (!parsed.success) {
      out[id] = { behavior: "optional" };
      continue;
    }

    if (parsed.data.behavior === "alwaysOn") {
      out[id] = { behavior: "optional" };
      continue;
    }

    out[id] = parsed.data;
  }
  return out;
}

type PreferenceSection = {
  title: string;
  description: string;
  linkedCategory: string;
};

/**
 * Reconcile a saved sections list against the categories the modal will
 * actually need to render. Saved sections keep their customized copy;
 * categories from `defaults` that aren't represented are folded in using the
 * default copy. The combined list is then handed to
 * `sortPreferencesModalSections`, which puts Essential first and orders the
 * rest alphabetically by `linkedCategory` (info-only sections keep their
 * original index, but the form never feeds those in here).
 *
 * Why this exists: persisting a config with hidden categories drops their
 * sections from the saved language (see
 * `ConsentManagerConfigForm.onSubmit`). On reload the form would then have
 * no section to render or persist for that category, so flipping it back
 * to optional would surface in `categories` but never produce a toggle.
 */
function mergePreferenceSections(
  saved: PreferenceSection[],
  defaults: PreferenceSection[],
): PreferenceSection[] {
  const present = new Set(saved.map((s) => s.linkedCategory));
  const missing = defaults.filter((s) => !present.has(s.linkedCategory));
  return sortPreferencesModalSections([...saved, ...missing]);
}

/**
 * Apply `mergePreferenceSections` to every translation in a conditional
 * config's language block. Conditional language is structurally validated
 * by the form schema, not by `safeParse` like the global, so we walk it
 * defensively and pass any translations with a non-array `sections` through
 * untouched.
 */
function healLanguageSections(
  language: ConditionalConfig["language"],
  defaults: PreferenceSection[],
): ConditionalConfig["language"] {
  const lifted = liftDescriptionFromIntroSection(
    language as unknown as PrefsLikeLanguage,
  );
  const nextTranslations: typeof language.translations = {};
  for (const [lang, t] of Object.entries(lifted.translations)) {
    const sections = t?.preferencesModal?.sections;
    if (!Array.isArray(sections)) {
      nextTranslations[lang] = t as (typeof language.translations)[string];
      continue;
    }
    nextTranslations[lang] = {
      ...t,
      preferencesModal: {
        ...t.preferencesModal,
        sections: mergePreferenceSections(
          sections as PreferenceSection[],
          defaults,
        ),
      },
    } as (typeof language.translations)[string];
  }
  return { ...language, translations: nextTranslations };
}

// Older configs persisted preferencesModal.position = "" when layout was "box" (position is unused for box).
// Coerce to "right" — the cookieconsent default — so legacy data still validates against the narrower enum.
function coerceLegacyPreferencesPosition(guiOptions: unknown): unknown {
  if (!guiOptions || typeof guiOptions !== "object") return guiOptions;
  const prefs = (guiOptions as { preferencesModal?: unknown }).preferencesModal;
  if (!prefs || typeof prefs !== "object") return guiOptions;
  if ((prefs as { position?: unknown }).position !== "") return guiOptions;
  return {
    ...guiOptions,
    preferencesModal: { ...prefs, position: "right" },
  };
}

function initialFreshpaintConfigValues({
  type,
  freshpaintConfig,
  categoryMapping,
}: ConsentManagementConfigVersion): ConsentManagerConfigSchema["freshpaintConfig"] {
  if (type !== "freshpaint") return;

  // Be defensive: version rows are JSONB-backed and `freshpaintConfig` is typed
  // optional at the API boundary, so corrupted / legacy data should still seed
  // a usable form instead of crashing initialization.
  const safeFreshpaintConfig =
    (freshpaintConfig as
      | NonNullable<ConsentManagementConfigVersion["freshpaintConfig"]>
      | null
      | undefined) ?? {};
  const safeLanguage =
    (safeFreshpaintConfig.language as
      | NonNullable<
          NonNullable<
            ConsentManagementConfigVersion["freshpaintConfig"]
          >["language"]
        >
      | null
      | undefined) ?? {};

  const categoryDescriptions =
    consentManagementIntegrations.freshpaint.defaultCategoryDescriptions;

  const defaultPreferencesSections = sortPreferencesModalSections(
    Object.keys(categoryMapping).map((categoryKey) => ({
      title: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1),
      description: categoryDescriptions[categoryKey.toLowerCase()],
      linkedCategory: categoryKey,
    })),
  );

  /*
   * These nested maps are a little bit complicated, so we do some validation on the incoming data before using the defaults
   */
  const guiOptionsValidationResult = guiOptionsSchema.safeParse(
    coerceLegacyPreferencesPosition(safeFreshpaintConfig.guiOptions),
  );

  const validatedGuiOptionDefaults = guiOptionsValidationResult.success
    ? guiOptionsValidationResult.data
    : ({
        consentModal: {
          layout: "box",
          position: "bottom right",
        },
        preferencesModal: {
          layout: "box",
          position: "right",
        },
      } satisfies GuiOptions);

  const liftedLanguageTranslations = safeLanguage.translations
    ? liftDescriptionFromIntroSection({
        translations: safeLanguage.translations,
      } as PrefsLikeLanguage).translations
    : safeLanguage.translations;

  const enTranslationValidationResult = translationSchema.safeParse(
    liftedLanguageTranslations?.en,
  );
  const validatedEnTranslation = enTranslationValidationResult.success
    ? {
        ...enTranslationValidationResult.data,
        preferencesModal: {
          ...enTranslationValidationResult.data.preferencesModal,
          // Self-heal: reattach a section for every category the modal might
          // need to render. Toggling a category to "Always disabled" (hidden)
          // strips its section from the persisted language at save time, so a
          // hidden->optional flip after reload would otherwise leave the
          // category with a `categories` entry but no section to drive a
          // toggle. Existing sections keep their customized copy; missing
          // ones get the default copy.
          sections: mergePreferenceSections(
            enTranslationValidationResult.data.preferencesModal.sections,
            defaultPreferencesSections,
          ),
        },
      }
    : {
        consentModal: {
          title: "We Value Your Privacy",
          description:
            'This website uses cookies and similar technologies to enhance your experience, analyze site performance, and deliver personalized content. By selecting “Accept Cookies” you agree to the storing of cookies on your device. By selecting "Reject Cookies" you agree to using only strictly necessary cookies on your device.',
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          showPreferencesBtn: "Manage Individual preferences",
        },
        preferencesModal: {
          title: "We Value Your Privacy",
          description: "",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          savePreferencesBtn: "Accept current selection",
          closeIconLabel: "Close modal",
          sections: defaultPreferencesSections,
        },
      };

  /**
   * end nested validation overwrites
   */

  const globalCategorySettings = defaultCategorySettings(
    categoryMapping,
    safeFreshpaintConfig.categorySettings as
      | Record<string, CategorySettings>
      | undefined,
  );
  const normalizedConditionalConfigs =
    safeFreshpaintConfig.conditionalConfigs?.map((config) => {
      const coercedGuiOptions = coerceLegacyPreferencesPosition(
        config.guiOptions,
      ) as ConditionalConfig["guiOptions"];
      return {
        ...config,
        guiOptions: coercedGuiOptions,
        customCSS: config.customCSS ?? "",
        autoShow: config.autoShow ?? safeFreshpaintConfig.autoShow ?? true,
        disablePageInteraction:
          config.disablePageInteraction ??
          safeFreshpaintConfig.disablePageInteraction ??
          false,
        hideConsentModalRejectAll:
          config.hideConsentModalRejectAll ??
          safeFreshpaintConfig.hideConsentModalRejectAll ??
          false,
        enableConsentModalDismiss:
          config.enableConsentModalDismiss ??
          safeFreshpaintConfig.enableConsentModalDismiss ??
          false,
        editSelections:
          config.editSelections ?? safeFreshpaintConfig.editSelections ?? false,
        disabled: config.disabled ?? false,
        // Conditional configs are full replacements: when a saved config has no
        // categorySettings (e.g. created before this feature), seed it from the
        // current global block so the conditional starts faithful to global.
        categorySettings: defaultCategorySettings(
          categoryMapping,
          config.categorySettings ?? globalCategorySettings,
        ),
        // Same self-heal as global: a previous hide+save destroys the section
        // for that category in this conditional's saved language. Reconcile
        // against the default template so a later un-hide still produces a
        // working modal toggle.
        language: healLanguageSections(
          config.language,
          defaultPreferencesSections,
        ),
        privacyPolicyUrl: config.privacyPolicyUrl ?? "",
        privacyPolicyLinkText: config.privacyPolicyLinkText ?? "",
      };
    }) ?? [];

  return {
    autoShow: safeFreshpaintConfig.autoShow ?? true,
    disablePageInteraction:
      safeFreshpaintConfig.disablePageInteraction ?? false,
    hideConsentModalRejectAll:
      safeFreshpaintConfig.hideConsentModalRejectAll ?? false,
    enableConsentModalDismiss:
      safeFreshpaintConfig.enableConsentModalDismiss ?? false,
    colorStyles: {
      ...defaultColorStyles,
      ...safeFreshpaintConfig.colorStyles,
    },
    editSelections: safeFreshpaintConfig.editSelections ?? false,
    gcmEnabled: safeFreshpaintConfig.gcmEnabled ?? false,
    iconColor: safeFreshpaintConfig.iconColor || "#d9d9d9",
    iconPosition: safeFreshpaintConfig.iconPosition ?? "right",
    customCSS: safeFreshpaintConfig.customCSS ?? "",
    guiOptions: validatedGuiOptionDefaults,
    language: {
      default: "en",
      autoDetect: "browser",
      ...safeLanguage,
      translations: {
        ...liftedLanguageTranslations,
        en: validatedEnTranslation,
      },
    },
    simpleBackgroundColor:
      safeFreshpaintConfig.simpleBackgroundColor || "#232323",
    simplePrimaryColor: safeFreshpaintConfig.simplePrimaryColor || "#c9bbbb",
    simpleSecondaryColor:
      safeFreshpaintConfig.simpleSecondaryColor || "#dedede",
    privacyPolicyUrl:
      safeFreshpaintConfig.privacyPolicyUrl ||
      safeLanguage.privacyPolicyUrl ||
      "",
    privacyPolicyLinkText: safeFreshpaintConfig.privacyPolicyLinkText ?? "",
    // Mirror the SDK's `determineGeotargetingBehavior`: an absent value means
    // geotargeting was never configured, and only the legacy
    // `regionTemplatesEnabled` flag opts a config into managed regions. Seeding
    // "managed" here instead would silently turn managed geotargeting on for
    // every legacy config on its next autosave.
    geotargetingBehavior:
      safeFreshpaintConfig.geotargetingBehavior ||
      (safeLanguage.regionTemplatesEnabled ? "managed" : "disabled"),
    conditionalConfigs: normalizedConditionalConfigs,
    categorySettings: globalCategorySettings,
  };
}
